/**
 * /intercompany — admin surface for cross-entity transactions.
 *
 * Three views:
 *   Pairs       — list account-pair maps (one per direction). Admin
 *                 creates a pair to enable IC transactions between two
 *                 entities; the system creates Due-from/Due-to
 *                 accounts in both entities' CoA automatically.
 *   Transactions — list of recent IC transactions across all entities.
 *                 Each row shows initiator → counterparty, amount,
 *                 type, and links to the two journals it produced.
 *   Balances    — outstanding Due-from/Due-to per pair, with imbalance
 *                 detection (drift between A's receivable + B's
 *                 mirror payable points at a data integrity issue).
 *
 * Plus a "+ New transaction" wizard that picks the type and posts.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Tab, Tabs, TextField,
} from "@mui/material";

import { api } from "@/lib/api";
import {
  entitiesListQueryOptions,
  icBalancesQueryOptions,
  icKeys,
  icPairsQueryOptions,
  icTransactionsQueryOptions,
  meQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import type { Entity, IntercompanyTxType } from "@/lib/types";


export const Route = createFileRoute("/_auth/intercompany/")({
  component: IntercompanyPage,
});


type View = "pairs" | "transactions" | "balances";

const TX_TYPES: { value: IntercompanyTxType; label: string; help: string }[] = [
  { value: "paid_on_behalf_of",
    label: "Paid on behalf of",
    help: "A pays a vendor invoice that belongs to B" },
  { value: "cash_transfer",
    label: "Cash transfer",
    help: "Cash moves between two non-parent/sub entities" },
  { value: "capital_injection",
    label: "Capital injection",
    help: "Parent funds Sub via Investment-in-Sub / Member Contributions" },
  { value: "distribution",
    label: "Distribution",
    help: "Sub pays a distribution to Parent" },
  { value: "shared_labor",
    label: "Shared labor",
    help: "A's employee worked on B's project" },
];


function IntercompanyPage() {
  const [view, setView] = useState<View>("transactions");
  const [formOpen, setFormOpen] = useState(false);

  const meQuery = useQuery(meQueryOptions());
  const isAdmin = meQuery.data?.role === "admin";

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Intercompany</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            cross-entity transactions
          </span>
          <div className="flex-1" />
          <button
            onClick={() => setFormOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New transaction
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs value={view} onChange={(_e, v) => setView(v as View)} sx={{ minHeight: 40 }}>
          <Tab value="transactions" label="Transactions" sx={{ minHeight: 40 }} />
          <Tab value="balances" label="Balances" sx={{ minHeight: 40 }} />
          <Tab value="pairs" label={isAdmin ? "Account Pairs (admin)" : "Account Pairs"} sx={{ minHeight: 40 }} />
        </Tabs>
      </div>

      <div className="flex-1 min-h-0 px-6 py-4 overflow-auto">
        {view === "transactions" && <TransactionsView />}
        {view === "balances" && <BalancesView />}
        {view === "pairs" && <PairsView isAdmin={isAdmin} />}
      </div>

      {formOpen && (
        <NewTransactionDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Transactions list
// ──────────────────────────────────────────────────────────────
function TransactionsView() {
  const txQuery = useQuery(icTransactionsQueryOptions());
  if (txQuery.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  const txs = txQuery.data ?? [];

  if (txs.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
        No intercompany transactions yet.
        <br/>
        Use <span className="font-medium">+ New transaction</span> to create one.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-gray-950 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-2">Date</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Initiator</th>
            <th className="text-left px-4 py-2">Counterparty</th>
            <th className="text-right px-4 py-2">Amount</th>
            <th className="text-left px-4 py-2">Description</th>
          </tr>
        </thead>
        <tbody>
          {txs.map((t) => (
            <tr key={t.id} className="border-t border-slate-100 dark:border-gray-800">
              <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
                {new Date(t.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-2">
                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded dark:bg-gray-800">
                  {t.transaction_type.replace(/_/g, " ")}
                </span>
              </td>
              <td className="px-4 py-2 font-mono text-xs">{t.initiator_code ?? "?"}</td>
              <td className="px-4 py-2 font-mono text-xs">{t.counterparty_code ?? "?"}</td>
              <td className="px-4 py-2 text-right font-mono">
                ${Number(t.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2,
                })}
              </td>
              <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
                {t.description}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Balances + imbalance detection
// ──────────────────────────────────────────────────────────────
function BalancesView() {
  const [asOf, setAsOf] = useState<string>("");
  const balancesQuery = useQuery(icBalancesQueryOptions(asOf || undefined));
  const data = balancesQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-700 dark:text-slate-300">As of:</label>
        <input
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
        />
        {asOf && (
          <button
            onClick={() => setAsOf("")}
            className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400"
          >
            clear
          </button>
        )}
      </div>

      {balancesQuery.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}

      {data && data.imbalanced_pairs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="text-sm font-semibold text-red-800 mb-2">
            ⚠ {data.imbalanced_pairs.length} imbalanced pair(s)
          </div>
          <div className="text-xs text-red-700">
            A's Due-from-B should exactly equal B's Due-to-A. A drift here
            usually points to one side of an IC transaction failing to
            post — investigate by drilling into the IC transaction log.
          </div>
        </div>
      )}

      {data && (
        <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-gray-950 dark:text-slate-300">
              <tr>
                <th className="text-left px-4 py-2">Entity</th>
                <th className="text-left px-4 py-2">Counterparty</th>
                <th className="text-right px-4 py-2">Due from (asset)</th>
                <th className="text-right px-4 py-2">Due to (liability)</th>
              </tr>
            </thead>
            <tbody>
              {data.pairs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    No intercompany pairs configured.
                  </td>
                </tr>
              )}
              {data.pairs.map((p) => (
                <tr key={`${p.entity_id}-${p.counterparty_entity_id}`}
                    className="border-t border-slate-100 dark:border-gray-800">
                  <td className="px-4 py-2 font-mono text-xs">{p.entity_code}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.counterparty_code}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {Number(p.receivable_balance).toLocaleString(undefined, {
                      minimumFractionDigits: 2, maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {Number(p.payable_balance).toLocaleString(undefined, {
                      minimumFractionDigits: 2, maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Pairs list + create
// ──────────────────────────────────────────────────────────────
function PairsView({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const pairsQuery = useQuery(icPairsQueryOptions());
  const entitiesQuery = useQuery(entitiesListQueryOptions());
  const [setupOpen, setSetupOpen] = useState(false);

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <button
            onClick={() => setSetupOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5"
          >
            + Setup new pair
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-gray-950 dark:text-slate-300">
            <tr>
              <th className="text-left px-4 py-2">Entity</th>
              <th className="text-left px-4 py-2">Counterparty</th>
              <th className="text-left px-4 py-2">Due from acct</th>
              <th className="text-left px-4 py-2">Due to acct</th>
            </tr>
          </thead>
          <tbody>
            {(pairsQuery.data ?? []).length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  No pairs yet.
                </td>
              </tr>
            )}
            {(pairsQuery.data ?? []).map((p) => (
              <tr key={p.id} className="border-t border-slate-100 dark:border-gray-800">
                <td className="px-4 py-2 font-mono text-xs">{p.entity_code}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.counterparty_code}</td>
                <td className="px-4 py-2 font-mono text-xs">
                  {p.receivable_account_code ?? "—"}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {p.payable_account_code ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {setupOpen && (
        <PairSetupDialog
          open={setupOpen}
          onClose={() => setSetupOpen(false)}
          entities={entitiesQuery.data ?? []}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: icKeys.all });
            toast.success("Intercompany pair created");
          }}
        />
      )}
    </div>
  );
}


function PairSetupDialog({
  open, onClose, entities, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  entities: Entity[];
  onCreated: () => void;
}) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  const create = useMutation({
    mutationFn: async () =>
      api("/api/intercompany/pairs", {
        method: "POST",
        body: { entity_a_id: a, entity_b_id: b },
      }),
    onSuccess: () => { onCreated(); onClose(); },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Setup intercompany pair</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Creates Due-from / Due-to accounts in BOTH entities' CoA, so
            future intercompany transactions auto-route through them.
          </div>
          <TextField select size="small" label="Entity A" value={a}
                     onChange={(e) => setA(e.target.value)}>
            {entities.map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.code} — {e.display_name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Entity B" value={b}
                     onChange={(e) => setB(e.target.value)}>
            {entities.filter((e) => e.id !== a).map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.code} — {e.display_name}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!a || !b || create.isPending}
                onClick={() => create.mutate()}>
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// New transaction wizard
// ──────────────────────────────────────────────────────────────
function NewTransactionDialog({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const entitiesQuery = useQuery(entitiesListQueryOptions());
  const entities = entitiesQuery.data ?? [];

  const [txType, setTxType] = useState<IntercompanyTxType>("paid_on_behalf_of");
  const [initiator, setInitiator] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseAccountCode, setExpenseAccountCode] = useState("5100");
  const [description, setDescription] = useState("");

  const submit = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        amount,
        description,
      };
      let endpoint = "";
      switch (txType) {
        case "paid_on_behalf_of":
          endpoint = "/api/intercompany/transactions/paid-on-behalf-of";
          body.payer_entity_id = initiator;
          body.owner_entity_id = counterparty;
          body.payer_cash_account_code = "1110";
          body.owner_expense_account_code = expenseAccountCode;
          break;
        case "cash_transfer":
          endpoint = "/api/intercompany/transactions/cash-transfer";
          body.from_entity_id = initiator;
          body.to_entity_id = counterparty;
          break;
        case "capital_injection":
          endpoint = "/api/intercompany/transactions/capital-injection";
          body.parent_entity_id = initiator;
          body.sub_entity_id = counterparty;
          break;
        case "distribution":
          endpoint = "/api/intercompany/transactions/distribution";
          body.sub_entity_id = initiator;
          body.parent_entity_id = counterparty;
          break;
        case "shared_labor":
          endpoint = "/api/intercompany/transactions/shared-labor";
          body.employer_entity_id = initiator;
          body.benefiting_entity_id = counterparty;
          break;
      }
      return api(endpoint, { method: "POST", body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: icKeys.all });
      toast.success("Intercompany transaction posted");
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const typeSpec = TX_TYPES.find((t) => t.value === txType)!;
  const initiatorLabel =
    txType === "paid_on_behalf_of" ? "Payer entity"
      : txType === "cash_transfer" ? "From entity"
      : txType === "capital_injection" ? "Parent entity"
      : txType === "distribution" ? "Sub entity"
      : "Employer entity";
  const counterpartyLabel =
    txType === "paid_on_behalf_of" ? "Owner of bill (entity that benefits)"
      : txType === "cash_transfer" ? "To entity"
      : txType === "capital_injection" ? "Sub entity"
      : txType === "distribution" ? "Parent entity"
      : "Benefiting entity";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New intercompany transaction</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select size="small" label="Transaction type" value={txType}
                     onChange={(e) => setTxType(e.target.value as IntercompanyTxType)}
                     helperText={typeSpec.help}>
            {TX_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={initiatorLabel} value={initiator}
                     onChange={(e) => setInitiator(e.target.value)}>
            {entities.map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.code} — {e.display_name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={counterpartyLabel} value={counterparty}
                     onChange={(e) => setCounterparty(e.target.value)}>
            {entities.filter((e) => e.id !== initiator).map((e) => (
              <MenuItem key={e.id} value={e.id}>{e.code} — {e.display_name}</MenuItem>
            ))}
          </TextField>
          <MoneyField size="small" label="Amount" value={amount}
                     onChange={setAmount} />
          {txType === "paid_on_behalf_of" && (
            <TextField size="small" label="Owner expense account code" value={expenseAccountCode}
                       onChange={(e) => setExpenseAccountCode(e.target.value)}
                       helperText="Account code on the OWNER's CoA where the expense lands (default 5100)" />
          )}
          <TextField size="small" label="Description" value={description}
                     onChange={(e) => setDescription(e.target.value)}
                     multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={
          !initiator || !counterparty || !amount || !description || submit.isPending
        } onClick={() => submit.mutate()}>
          {submit.isPending ? "Posting…" : "Post"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
